/**
 * Persona Kütüphanesi transport katmanı.
 *
 * `authClient.ts`'in `request()` sarmalayıcısını KASTEN kullanmaz: o wrapper
 * `Error(string)` fırlatıp status ve `code`'u yutuyor, çağıranlar da
 * `msg.includes("404")` gibi string eşlemesi yapmak zorunda kalıyor. Burada
 * `wizardClient.ts`'in tipli hata deseni izlenir — UI, 403 (yetki) ile 422
 * (yetersiz örnek) arasını ayırt edebilmek zorunda, çünkü ikisine verilecek
 * yanıt bambaşka.
 *
 * Kimlik doğrulama modeli SPA'nın geri kalanıyla aynı: cookie tabanlı oturum
 * (`credentials: "include"`) + mutasyonlarda CSRF double-submit.
 */
import type {
  CollectionDocument,
  ConsolidationResult,
  FeedbackVerdict,
  GrowthLesson,
  LibraryCatalog,
  LibraryPersona,
  LibraryPersonaBrief,
  LibrarySearchHit,
  MemoryCollection,
  MemoryKind,
  PersonaDetailBundle,
  PersonaDraft,
  PersonaFeedback,
  PersonaMemory,
  PersonaRevision,
  PersonaTeam,
  PersonaUnit,
  SessionFeedbackState,
  StyleProfile,
  UnitKind,
} from "../domain/personaLibrary";
import { getCsrfToken, withCsrfHeader } from "./csrf";

const BASE = "/api/debate/persona-library";

/** Tipli API hatası — status ve backend `code`'u korur. */
export class LibraryApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details: unknown;

  constructor(status: number, code: string, message: string, details?: unknown) {
    super(message);
    this.name = "LibraryApiError";
    this.status = status;
    this.code = code;
    this.details = details;
  }

  /** Özellik sunucuda kapalı ya da servis ayakta değil. */
  get isUnavailable(): boolean {
    return this.status === 404 || this.status === 503;
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const headers: Record<string, string> = withCsrfHeader({
    "Content-Type": "application/json",
    Accept: "application/json",
    ...(init?.headers as Record<string, string> | undefined),
  });
  let res: Response;
  try {
    res = await fetch(`${BASE}${path}`, {
      ...init,
      headers,
      credentials: "include",
    });
  } catch (err) {
    // Ağ kopması — status yok. 0 ile işaretleyip UI'ın "sunucuya
    // ulaşılamıyor" ile "sunucu reddetti"yi ayırmasını sağlıyoruz.
    throw new LibraryApiError(
      0,
      "network",
      err instanceof Error ? err.message : "Sunucuya ulaşılamadı.",
    );
  }

  if (res.status === 204) return undefined as T;

  const raw = await res.text();
  let body: unknown = null;
  if (raw) {
    try {
      body = JSON.parse(raw);
    } catch {
      body = null;
    }
  }

  if (!res.ok) {
    const env = (body ?? {}) as { code?: string; message?: string; detail?: string; details?: unknown };
    throw new LibraryApiError(
      res.status,
      env.code ?? `http_${res.status}`,
      env.message ?? env.detail ?? `İstek başarısız (HTTP ${res.status}).`,
      env.details,
    );
  }
  return body as T;
}

/**
 * Multipart yükleme + ilerleme raporu.
 *
 * `fetch` yükleme ilerlemesini raporlamaz (yalnızca indirme), bu yüzden
 * `ragClient.ts` ile aynı gerekçeyle XHR kullanılır: 20MB'lık bir PDF'te
 * ilerlemesiz bir spinner kullanıcıya donmuş gibi görünüyor.
 */
function upload<T>(
  path: string,
  file: File,
  onProgress?: (pct: number) => void,
): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const form = new FormData();
    form.append("file", file);

    const xhr = new XMLHttpRequest();
    xhr.open("POST", `${BASE}${path}`, true);
    xhr.withCredentials = true;
    const csrf = getCsrfToken();
    if (csrf) xhr.setRequestHeader("X-Macdm-Csrf", csrf);

    xhr.upload.onprogress = (e) => {
      if (onProgress && e.lengthComputable) {
        onProgress(Math.round((e.loaded / e.total) * 100));
      }
    };
    xhr.onerror = () =>
      reject(new LibraryApiError(0, "network", "Yükleme sırasında bağlantı koptu."));
    xhr.onload = () => {
      let parsed: unknown = null;
      try {
        parsed = JSON.parse(xhr.responseText);
      } catch {
        parsed = null;
      }
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve(parsed as T);
        return;
      }
      const env = (parsed ?? {}) as { code?: string; message?: string; detail?: string };
      reject(
        new LibraryApiError(
          xhr.status,
          env.code ?? `http_${xhr.status}`,
          env.message ?? env.detail ?? `Yükleme başarısız (HTTP ${xhr.status}).`,
        ),
      );
    };
    xhr.send(form);
  });
}

// ---------------------------------------------------------------------------
// Katalog
// ---------------------------------------------------------------------------

/**
 * Eksen/enum kataloğu. Süreç boyunca sabit olduğu için modül içinde
 * önbelleklenir ve eşzamanlı çağrılar tek isteğe indirgenir (persona
 * düzenleyici her açılışta bunu istiyor).
 */
let catalogCache: LibraryCatalog | null = null;
let catalogInflight: Promise<LibraryCatalog> | null = null;

export async function fetchCatalog(): Promise<LibraryCatalog> {
  if (catalogCache) return catalogCache;
  if (catalogInflight) return catalogInflight;
  catalogInflight = request<LibraryCatalog>("/axes")
    .then((data) => {
      catalogCache = data;
      return data;
    })
    .finally(() => {
      catalogInflight = null;
    });
  return catalogInflight;
}

// ---------------------------------------------------------------------------
// Persona CRUD
// ---------------------------------------------------------------------------

export async function listPersonas(opts?: {
  search?: string;
  tag?: string;
  unitId?: string;
  unassigned?: boolean;
  includeArchived?: boolean;
}): Promise<LibraryPersonaBrief[]> {
  const qs = new URLSearchParams();
  if (opts?.search) qs.set("search", opts.search);
  if (opts?.tag) qs.set("tag", opts.tag);
  if (opts?.unitId) qs.set("unit_id", opts.unitId);
  if (opts?.unassigned) qs.set("unassigned", "true");
  if (opts?.includeArchived) qs.set("include_archived", "true");
  const suffix = qs.toString() ? `?${qs.toString()}` : "";
  const data = await request<{ personas: LibraryPersonaBrief[] }>(`/personas${suffix}`);
  return data.personas;
}

// ---------------------------------------------------------------------------
// Organizasyon ağacı
// ---------------------------------------------------------------------------

export interface UnitListResponse {
  units: PersonaUnit[];
  /** Hiçbir birime atanmamış persona sayısı. */
  unassignedCount: number;
  kinds: UnitKind[];
}

export async function listUnits(includeArchived = false): Promise<UnitListResponse> {
  const suffix = includeArchived ? "?include_archived=true" : "";
  return request<UnitListResponse>(`/units${suffix}`);
}

export async function createUnit(payload: {
  name: string;
  parentId?: string | null;
  kind?: string;
  description?: string;
  mission?: string;
  accentColor?: string;
}): Promise<{ id: string; name: string; depth: number }> {
  const data = await request<{ unit: { id: string; name: string; depth: number } }>(
    "/units",
    { method: "POST", body: JSON.stringify(payload) },
  );
  return data.unit;
}

export async function updateUnit(
  unitId: string,
  payload: Record<string, unknown>,
): Promise<void> {
  await request(`/units/${unitId}`, {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
}

/**
 * Birimi sil. `cascade=false` (varsayılan) alt birimleri ebeveyne terfi
 * ettirir; personalar hiçbir durumda silinmez, atanmamışa düşer.
 */
export async function deleteUnit(unitId: string, cascade = false): Promise<number> {
  const data = await request<{ removed: number }>(
    `/units/${unitId}?cascade=${cascade ? "true" : "false"}`,
    { method: "DELETE" },
  );
  return data.removed;
}

/** `unitId=null` personayı atanmamışa alır. */
export async function assignPersonaUnit(
  personaId: string,
  unitId: string | null,
): Promise<void> {
  await request(`/personas/${personaId}/unit`, {
    method: "POST",
    body: JSON.stringify({ unitId }),
  });
}

/**
 * Bir birimin KENDİ belge bölümleri + atalarından MİRAS aldıkları.
 *
 * Miras salt okunurdur: bir departmanın belgesi altındaki ekiplere
 * otomatik akar ama oradan düzenlenemez — kaynağı tek yerde tutmak,
 * "hangi kopyası güncel?" sorusunu tamamen ortadan kaldırır.
 */
export async function listUnitCollections(
  unitId: string,
): Promise<{ collections: MemoryCollection[]; inherited: MemoryCollection[] }> {
  return request(`/units/${unitId}/collections`);
}

export async function createUnitCollection(
  unitId: string,
  payload: {
    name: string;
    description?: string;
    kind?: string;
    retrievalMode?: string;
    topK?: number;
  },
): Promise<MemoryCollection> {
  const data = await request<{ collection: MemoryCollection }>(
    `/units/${unitId}/collections`,
    { method: "POST", body: JSON.stringify(payload) },
  );
  return data.collection;
}

export async function synthesizePersona(input: {
  title: string;
  hint?: string;
  role?: string | null;
}): Promise<PersonaDraft> {
  const data = await request<{ draft: PersonaDraft }>("/personas/synthesize", {
    method: "POST",
    body: JSON.stringify({
      title: input.title,
      hint: input.hint ?? "",
      role: input.role ?? null,
    }),
  });
  return data.draft;
}

export async function createPersona(
  payload: Record<string, unknown>,
): Promise<LibraryPersona> {
  const data = await request<{ persona: LibraryPersona }>("/personas", {
    method: "POST",
    body: JSON.stringify(payload),
  });
  return data.persona;
}

export async function fetchPersona(id: string): Promise<PersonaDetailBundle> {
  return request<PersonaDetailBundle>(`/personas/${id}`);
}

export async function updatePersona(
  id: string,
  payload: Record<string, unknown>,
): Promise<LibraryPersona> {
  const data = await request<{ persona: LibraryPersona }>(`/personas/${id}`, {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
  return data.persona;
}

export async function deletePersona(id: string): Promise<void> {
  await request<{ ok: boolean }>(`/personas/${id}`, { method: "DELETE" });
}

export async function clonePersona(
  id: string,
  title?: string,
): Promise<LibraryPersona> {
  const data = await request<{ persona: LibraryPersona }>(`/personas/${id}/clone`, {
    method: "POST",
    body: JSON.stringify({ title: title ?? "" }),
  });
  return data.persona;
}

export async function importPersonaFromDebate(
  personaId: string,
  title?: string,
): Promise<LibraryPersona> {
  const data = await request<{ persona: LibraryPersona }>("/personas/import-from-debate", {
    method: "POST",
    body: JSON.stringify({ personaId, title: title ?? "" }),
  });
  return data.persona;
}

/** LLM ile talimatı yeniden yazar; KAYDETMEZ — sonucu kullanıcı onaylar. */
export async function refineInstruction(
  id: string,
  directive: string,
): Promise<{ systemInstruction: string; changed: boolean }> {
  return request(`/personas/${id}/refine`, {
    method: "POST",
    body: JSON.stringify({ directive }),
  });
}

export async function uploadAvatar(
  id: string,
  file: File,
  onProgress?: (pct: number) => void,
): Promise<string> {
  const data = await upload<{ avatarUrl: string }>(
    `/personas/${id}/avatar`,
    file,
    onProgress,
  );
  return data.avatarUrl;
}

export async function listRevisions(id: string): Promise<PersonaRevision[]> {
  const data = await request<{ revisions: PersonaRevision[] }>(`/personas/${id}/revisions`);
  return data.revisions;
}

export async function revertRevision(
  id: string,
  revision: number,
): Promise<LibraryPersona> {
  const data = await request<{ persona: LibraryPersona }>(
    `/personas/${id}/revisions/${revision}/revert`,
    { method: "POST" },
  );
  return data.persona;
}

// ---------------------------------------------------------------------------
// Hafıza
// ---------------------------------------------------------------------------

export async function listMemories(
  id: string,
  opts?: { kind?: MemoryKind | ""; includeArchived?: boolean },
): Promise<PersonaMemory[]> {
  const qs = new URLSearchParams();
  if (opts?.kind) qs.set("kind", opts.kind);
  if (opts?.includeArchived) qs.set("include_archived", "true");
  const suffix = qs.toString() ? `?${qs.toString()}` : "";
  const data = await request<{ memories: PersonaMemory[] }>(
    `/personas/${id}/memories${suffix}`,
  );
  return data.memories;
}

export async function createMemory(
  id: string,
  payload: {
    content: string;
    kind: MemoryKind;
    context?: string;
    keywords?: string[];
    importance?: number;
    isPinned?: boolean;
  },
): Promise<string> {
  const data = await request<{ id: string }>(`/personas/${id}/memories`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
  return data.id;
}

export async function patchMemory(
  memoryId: string,
  payload: { content?: string; importance?: number; isPinned?: boolean },
): Promise<void> {
  await request(`/memories/${memoryId}`, {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
}

/** `hard=false` (varsayılan) bi-temporal arşivleme yapar, satırı silmez. */
export async function deleteMemory(memoryId: string, hard = false): Promise<void> {
  await request(`/memories/${memoryId}?hard=${hard ? "true" : "false"}`, {
    method: "DELETE",
  });
}

export async function forceReflect(id: string): Promise<number> {
  const data = await request<{ reflections: number }>(`/personas/${id}/reflect`, {
    method: "POST",
  });
  return data.reflections;
}

// ---------------------------------------------------------------------------
// Belge hafıza bölümleri
// ---------------------------------------------------------------------------

export async function listCollections(id: string): Promise<MemoryCollection[]> {
  const data = await request<{ collections: MemoryCollection[] }>(
    `/personas/${id}/collections`,
  );
  return data.collections;
}

export async function createCollection(
  id: string,
  payload: {
    name: string;
    description?: string;
    kind?: string;
    retrievalMode?: string;
    injectionWeight?: number;
    topK?: number;
  },
): Promise<MemoryCollection> {
  const data = await request<{ collection: MemoryCollection }>(
    `/personas/${id}/collections`,
    { method: "POST", body: JSON.stringify(payload) },
  );
  return data.collection;
}

export async function patchCollection(
  collectionId: string,
  payload: Record<string, unknown>,
): Promise<void> {
  await request(`/collections/${collectionId}`, {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
}

export async function deleteCollection(collectionId: string): Promise<void> {
  await request(`/collections/${collectionId}`, { method: "DELETE" });
}

export async function listCollectionDocuments(
  collectionId: string,
): Promise<CollectionDocument[]> {
  const data = await request<{ documents: CollectionDocument[] }>(
    `/collections/${collectionId}/documents`,
  );
  return data.documents;
}

export async function uploadCollectionDocument(
  collectionId: string,
  file: File,
  onProgress?: (pct: number) => void,
): Promise<{ id: string; chunkCount: number }> {
  const data = await upload<{ document: { id: string; chunkCount: number } }>(
    `/collections/${collectionId}/documents`,
    file,
    onProgress,
  );
  return data.document;
}

export async function deleteCollectionDocument(documentId: string): Promise<void> {
  await request(`/documents/${documentId}`, { method: "DELETE" });
}

/**
 * İndirme adresi — düz bir `<a href>` navigasyonu için, `fetch` DEĞİL.
 *
 * Aynı origin GET, tarayıcı çerezi zaten otomatik gönderir; CSRF çift-
 * gönderim şeması sadece mutasyonlarda (`request()` sarmalayıcısı) zorunlu.
 * `fmt="text"` orijinal dosya mevcut olsa bile dizinlenen metni zorlar —
 * "sisteme ne girdi gerçekten?" sorusunu doğrulamak için.
 */
export function documentDownloadUrl(
  documentId: string,
  fmt: "original" | "text" = "original",
): string {
  return `${BASE}/documents/${documentId}/download?fmt=${fmt}`;
}

// ---------------------------------------------------------------------------
// Vektör arama — bölüm / birim / persona ölçeğinde
// ---------------------------------------------------------------------------

async function runSearch(
  path: string,
  query: string,
  topK: number,
): Promise<LibrarySearchHit[]> {
  const qs = new URLSearchParams({ q: query, topK: String(topK) });
  const data = await request<{ hits: LibrarySearchHit[] }>(`${path}?${qs.toString()}`);
  return data.hits;
}

/** Bir bölümün İÇİNDE anlamsal arama — "bu belgede X var mıydı?" */
export async function searchCollection(
  collectionId: string,
  query: string,
  topK = 8,
): Promise<LibrarySearchHit[]> {
  return runSearch(`/collections/${collectionId}/search`, query, topK);
}

/** Bir birimin TÜM havuzunda (kendi + üst birimlerden miras) arama. */
export async function searchUnit(
  unitId: string,
  query: string,
  topK = 8,
): Promise<LibrarySearchHit[]> {
  return runSearch(`/units/${unitId}/search`, query, topK);
}

/** Bir personanın gördüğü HER ŞEYDE (kendi + birim mirası) arama. */
export async function searchPersona(
  personaId: string,
  query: string,
  topK = 8,
): Promise<LibrarySearchHit[]> {
  return runSearch(`/personas/${personaId}/search`, query, topK);
}

// ---------------------------------------------------------------------------
// Stil parmak izi
// ---------------------------------------------------------------------------

export async function fetchStyle(id: string): Promise<StyleProfile> {
  const data = await request<{ style: StyleProfile }>(`/personas/${id}/style`);
  return data.style;
}

export async function buildStyle(id: string): Promise<StyleProfile> {
  const data = await request<{ style: StyleProfile }>(`/personas/${id}/style/build`, {
    method: "POST",
  });
  return data.style;
}

// ---------------------------------------------------------------------------
// Geri bildirim ve gelişim
// ---------------------------------------------------------------------------

export async function submitFeedback(payload: {
  turnId?: string;
  sessionId?: string;
  personaId?: string;
  libraryPersonaId?: string;
  verdict: FeedbackVerdict;
  rating?: number | null;
  note?: string;
  excerpt?: string;
  dimensions?: Record<string, number>;
}): Promise<{
  id: string;
  libraryPersonaId: string | null;
  consolidation: ConsolidationResult;
}> {
  return request("/feedback", { method: "POST", body: JSON.stringify(payload) });
}

export async function fetchSessionFeedback(
  sessionId: string,
): Promise<SessionFeedbackState> {
  return request<SessionFeedbackState>(`/sessions/${sessionId}/feedback`);
}

export async function listPersonaFeedback(id: string): Promise<PersonaFeedback[]> {
  const data = await request<{ feedback: PersonaFeedback[] }>(`/personas/${id}/feedback`);
  return data.feedback;
}

export async function listLessons(id: string): Promise<GrowthLesson[]> {
  const data = await request<{ lessons: GrowthLesson[] }>(`/personas/${id}/lessons`);
  return data.lessons;
}

export async function patchLesson(
  lessonId: string,
  payload: { lesson?: string; polarity?: string; isActive?: boolean; weight?: number },
): Promise<void> {
  await request(`/lessons/${lessonId}`, {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
}

export async function deleteLesson(lessonId: string): Promise<void> {
  await request(`/lessons/${lessonId}`, { method: "DELETE" });
}

export async function consolidateGrowth(id: string): Promise<ConsolidationResult> {
  return request<ConsolidationResult>(`/personas/${id}/consolidate`, { method: "POST" });
}

// ---------------------------------------------------------------------------
// Takımlar
// ---------------------------------------------------------------------------

export async function listTeams(includeArchived = false): Promise<PersonaTeam[]> {
  const suffix = includeArchived ? "?include_archived=true" : "";
  const data = await request<{ teams: PersonaTeam[] }>(`/teams${suffix}`);
  return data.teams;
}

export async function createTeam(payload: {
  name: string;
  description?: string;
  mission?: string;
  defaultTopology?: string;
  defaultMaxRounds?: number | null;
  accentColor?: string;
  tags?: string[];
  memberIds?: string[];
}): Promise<{ id: string; name: string }> {
  const data = await request<{ team: { id: string; name: string } }>("/teams", {
    method: "POST",
    body: JSON.stringify(payload),
  });
  return data.team;
}

export async function updateTeam(
  teamId: string,
  payload: Record<string, unknown>,
): Promise<void> {
  await request(`/teams/${teamId}`, {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
}

export async function deleteTeam(teamId: string): Promise<void> {
  await request(`/teams/${teamId}`, { method: "DELETE" });
}
